import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, Volume2, VolumeX } from "lucide-react";
import { api } from "@/lib/api";

/**
 * LandingReelHero — SINGLE consolidated reel on the landing.
 *
 * Founder plain-ink 2026-08-16:
 *   "The Reel is the quality we want... You have 2 that are the same, stacked.
 *    One has the music, the other has the words, neither is as good as the reel
 *    in video quality. We want the words, the music, and the reel-quality, ALL
 *    on the landing page. Compress them into one, get rid of duplicates. And
 *    also, on my end music does not play. Still."
 *
 * This component now carries all three:
 *   - The reel-quality 2:00 master film (letterboxed cinema, not object-cover)
 *   - The music (audio track lives on the file · tap-to-unmute button forces
 *     both muted=false AND .play() so the reel actually starts making sound
 *     even when the initial autoplay landed paused)
 *   - The words (world name + caption overlay synced to reel.currentTime,
 *     pulled from /api/film/worlds — the 12 worlds at 10s each)
 *
 * WorldShowcase has been REMOVED from the landing (was the duplicate).
 */

const WORLD_STYLE = {
  "Neon Requiem":     { font: "'Chakra Petch', sans-serif",       color: "#FF4FD8" },
  "Gilded Cage":      { font: "'Playfair Display', serif",        color: "#E8C36A" },
  "The Nine Threads": { font: "'Cinzel', serif",                  color: "#F0C64B" },
  "First Fire":       { font: "'IM Fell English SC', serif",      color: "#FF7A45" },
  "Last Lighthouse":  { font: "'Cormorant Garamond', serif",      color: "#A9D3F0" },
  "Hostile Takeover": { font: "'Bebas Neue', sans-serif",         color: "#F0554E" },
  "Whodunnit":        { font: "'Playfair Display', serif",        color: "#D66B7E" },
  "Soljins":          { font: "'Cinzel', serif",                  color: "#3DE0C8" },
  "Waking Maw":       { font: "'IM Fell English SC', serif",      color: "#C0392B" },
  "Far Dark":         { font: "'Orbitron', sans-serif",           color: "#9CC0F5" },
  "McPirl":           { font: "'Bebas Neue', sans-serif",         color: "#A5D66A" },
  "Convergence":      { font: "'Cinzel Decorative', serif",       color: "#ECD08A" },
};
const FALLBACK_STYLE = { font: "'Cormorant Garamond', serif", color: "#ff5a00" };

const REEL_URL = "/samples/ad-campaign/master_v2_2m20.mp4";
const REEL_POSTER = "/samples/ad-campaign/master_v2_poster.jpg";
const REEL_DURATION = 120; // seconds — 12 worlds × 10s each

export default function LandingReelHero() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [captions, setCaptions] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);

  // Pull the caption list once — 12 worlds in the same order as the reel.
  useEffect(() => {
    api.get("/film/worlds").then((r) => {
      setCaptions(r.data.captions || []);
    }).catch(() => {});
  }, []);

  // Sync the active world caption to the reel's currentTime.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      if (!captions.length) return;
      const t = v.currentTime || 0;
      const secPerWorld = REEL_DURATION / captions.length;
      const idx = Math.min(captions.length - 1, Math.floor(t / secPerWorld));
      setActiveIdx((prev) => (prev === idx ? prev : idx));
    };
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, [captions.length]);

  // Robust unmute: browsers may leave the reel paused if the initial muted
  // autoplay hit any hiccup. The tap has to unmute AND force a fresh .play().
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const nextMuted = !v.muted;
    v.muted = nextMuted;
    setMuted(nextMuted);
    // Always try to (re-)start playback after any mute-state change.
    try {
      const p = v.play?.();
      if (p && p.catch) p.catch(() => { /* Autoplay may still fail on some UAs; harmless. */ });
    } catch (_) { /* noop */ }
  };

  const active = captions[activeIdx];
  const style = (active && WORLD_STYLE[active.name]) || FALLBACK_STYLE;
  const titleColor = active?.veiled ? "#C0392B" : style.color;

  return (
    <section
      id="landing-reel"
      data-testid="landing-reel-hero"
      className="relative w-full bg-black flex items-center justify-center py-6 sm:py-8"
    >
      {/* Cinema letterbox — matches /reel page quality, no object-cover crop.
          Height-first sizing so captions + world-name never clip the viewport
          on wide 16:10 monitors. Founder plain-ink 2026-08-17+: bottom of
          images and words were getting cut off — shrunk to fit viewport
          with breathing room. object-contain letterboxes 16:9 source cleanly
          inside a height-constrained container. */}
      <div className="relative w-full max-w-[1440px] max-h-[75svh] mx-auto shadow-[0_40px_120px_-20px_rgba(255,90,0,0.28)]"
           style={{ height: "min(75svh, calc((100vw - 32px) * 9 / 16))" }}>
        <video
          ref={videoRef}
          data-testid="landing-reel-video"
          poster={REEL_POSTER}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
          className="w-full h-full object-contain bg-black"
        >
          {/* Explicit type hint helps browsers with mis-typed CDN content-type. */}
          <source src={REEL_URL} type="video/mp4" />
        </video>

        {/* Vignette gradients — keep the captions readable without stamping over the frame. */}
        <div aria-hidden className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/70 via-black/25 to-transparent pointer-events-none" />
        <div aria-hidden className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-black/80 via-black/35 to-transparent pointer-events-none" />

        {/* World name — top-center, synced to reel.currentTime */}
        {active && (
          <div className="absolute inset-x-0 top-[6%] flex justify-center px-6 text-center z-10 pointer-events-none">
            <AnimatePresence initial={false}>
              <motion.h2
                key={`name-${activeIdx}`}
                initial={{ opacity: 0, y: -14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                data-testid="landing-reel-world-name"
                className="absolute uppercase leading-[0.92] drop-shadow-[0_3px_34px_rgba(0,0,0,0.9)]"
                style={{
                  fontFamily: style.font,
                  color: titleColor,
                  fontSize: "clamp(2.25rem, 7.5vw, 6.5rem)",
                  letterSpacing: "0.015em",
                }}
              >
                {active.name}
              </motion.h2>
            </AnimatePresence>
          </div>
        )}

        {/* Caption — bottom-anchored, synced to reel.currentTime */}
        {active && (
          <div className="absolute inset-x-0 bottom-[10%] flex justify-center px-6 text-center z-10 pointer-events-none">
            <AnimatePresence initial={false}>
              <motion.p
                key={`cap-${activeIdx}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                data-testid="landing-reel-caption"
                className="font-display italic text-lg sm:text-2xl md:text-3xl lg:text-4xl text-[#f5f0e8] max-w-[92%] sm:max-w-3xl leading-tight drop-shadow-[0_2px_22px_rgba(0,0,0,0.9)]"
              >
                {active.caption}
              </motion.p>
            </AnimatePresence>
          </div>
        )}

        {/* Tap-to-unmute — big, obvious, forces play even if paused. */}
        {muted && (
          <button
            onClick={toggleMute}
            data-testid="landing-reel-unmute"
            className="absolute top-5 right-5 z-20 flex items-center gap-2 bg-black/75 backdrop-blur-md border border-[#ff5a00]/50 text-[#ff5a00] hover:bg-[#ff5a00] hover:text-black transition-colors px-4 py-2 font-mono text-[10px] uppercase tracking-[0.32em]"
            aria-label="Unmute reel"
          >
            <VolumeX className="w-3.5 h-3.5" />
            <span>Tap for sound</span>
          </button>
        )}
        {!muted && (
          <button
            onClick={toggleMute}
            data-testid="landing-reel-mute"
            className="absolute top-5 right-5 z-20 flex items-center gap-2 bg-black/70 backdrop-blur-md border border-white/20 text-[#ededed] hover:border-[#ff5a00]/60 transition-colors px-4 py-2 font-mono text-[10px] uppercase tracking-[0.32em]"
            aria-label="Mute reel"
          >
            <Volume2 className="w-3.5 h-3.5" />
            <span>Mute</span>
          </button>
        )}
      </div>

      {/* Scroll cue */}
      <motion.a
        href="#doors"
        data-testid="landing-reel-scroll-cue"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.9 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 text-[#a1a1aa] hover:text-[#ff5a00] transition-colors"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.36em]">
          The Constellation
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="w-4 h-4" />
        </motion.span>
      </motion.a>
    </section>
  );
}
